import FleetModal, { useAsyncModal } from '@/libs/antd/components/Modal';
import FleetTitle from '@/libs/antd/components/Title';
import { useForm } from '@/libs/antd/form';
import ANTD_DEFAULT_PROPS from '@/libs/antd/props';
import { UploadOutlined } from '@ant-design/icons';
import rule from '@/libs/antd/rules';
import {
	FileData,
	ProspectEmploymentContract,
} from '@/libs/fleet-api/services/types';
import {
	Col,
	DatePicker,
	Divider,
	Form,
	Input,
	InputNumber,
	Row,
	Select,
	Upload,
	UploadFile,
} from 'antd';
import TextArea from 'antd/es/input/TextArea';
import dayjs from 'dayjs';
import { FC } from 'react';
import { useMutation } from '@tanstack/react-query';
import { uploader } from '@/libs/fleet-api/uploader';
import {
	useSelectBouwheer,
	useSelectPaymentMethod,
} from '@/libs/fleet-api/hooks/master-data';
import { inputCurrency } from '@/libs/antd/input-number';

type EmploymentContractForm = ProspectEmploymentContract;

export const useEmploymentContractModal = () => {
	return useAsyncModal<EmploymentContractForm, EmploymentContractForm>();
};

const EmploymentContractModal: FC<
	ReturnType<typeof useEmploymentContractModal> & {
		customerId: string;
		prospectId: string;
	}
> = (props) => {
	const { form, fieldName } = useForm<EmploymentContractForm>();
	const selectPaymentMethod = useSelectPaymentMethod();
	const selectBouwheer = useSelectBouwheer();

	const uploadMutation = useMutation({
		mutationFn: uploader,
	});

	return (
		<FleetModal
			{...props.modal}
			width={940}
			closable={false}
			buttonPosition="end"
			okText="SAVE"
			cancelText="BACK"
			onOk={async () => {
				const values = await form.validateFields();
				props.close(values);
			}}
			afterOpenChange={(isOpen) => {
				if (!isOpen) {
					form.resetFields();
				} else if (props.data) {
					console.log(props.data);
					form.setFieldsValue(props.data);
				}
			}}
		>
			<FleetTitle>Kontrak Kerja</FleetTitle>
			<Form
				{...ANTD_DEFAULT_PROPS.FORM}
				form={form}
				labelCol={{ span: 5 }}
				wrapperCol={{ span: 19 }}
				style={{ marginTop: 20 }}
			>
				<Form.Item
					name={fieldName('id')}
					noStyle
				/>
				<Form.Item
					name={fieldName('prospectId')}
					noStyle
				/>
				<Form.Item
					name={fieldName(['bouwheer', 'id'])}
					noStyle
				/>
				<Row gutter={[40, 0]}>
					<Col span={12}>
						<Form.Item
							label="Bouwheer"
							labelCol={{ span: 8 }}
							name={fieldName(['bouwheer'])}
							getValueFromEvent={(_, option) => option?.data}
							getValueProps={(value) => {
								if (!value) return {};
								return {
									value: {
										value: value.id,
										label: value.name,
									},
								};
							}}
						>
							<Select {...selectBouwheer} />
						</Form.Item>
						<Divider />
					</Col>
				</Row>
				<Row gutter={[40, 0]}>
					<Col span={12}>
						<Form.Item
							label="Bouwheer Name"
							labelCol={{ span: 8 }}
							name={fieldName(['bouwheer', 'name'])}
						>
							<Input disabled />
						</Form.Item>
						<Divider />
						<Form.Item
							label="Kelas Industri"
							labelCol={{ span: 8 }}
							name={fieldName(['bouwheer', 'industrySector', 'industryClass'])}
							rules={[rule.required()]}
						>
							<Input disabled />
						</Form.Item>
						<Divider />
						<Form.Item
							label="Sektor Industri LV 4"
							labelCol={{ span: 8 }}
							name={fieldName(['bouwheer', 'industrySector', 'sectorLevel4'])}
						>
							<Input disabled />
						</Form.Item>
						<Divider />
						<Form.Item
							label="Sektor Industri LV 1"
							labelCol={{ span: 8 }}
							name={fieldName(['bouwheer', 'industrySector', 'sectorLevel1'])}
						>
							<Input disabled />
						</Form.Item>
						<Divider />
						<Form.Item
							label="Nama Pekerjaan"
							labelCol={{ span: 8 }}
							name={fieldName('jobTitle')}
							rules={[rule.required()]}
						>
							<Input />
						</Form.Item>
					</Col>
					<Col span={12}>
						<Form.Item
							label="Bouwheer Type"
							labelCol={{ span: 8 }}
							name={fieldName(['bouwheer', 'type'])}
						>
							<Input disabled />
						</Form.Item>
						<Divider />
						<Form.Item
							label="Sektor Industri LV 5"
							labelCol={{ span: 8 }}
							name={fieldName(['bouwheer', 'industrySector', 'sectorLevel5'])}
						>
							<Input disabled />
						</Form.Item>
						<Divider />
						<Form.Item
							label="Sektor Industri LV 2"
							labelCol={{ span: 8 }}
							name={fieldName(['bouwheer', 'industrySector', 'sectorLevel2'])}
						>
							<Input disabled />
						</Form.Item>
						<Divider />
						<Form.Item
							label="Sektor Industri LV 3"
							labelCol={{ span: 8 }}
							name={fieldName(['bouwheer', 'industrySector', 'sectorLevel3'])}
						>
							<Input disabled />
						</Form.Item>
						<Divider />
						<Form.Item
							label="No.SPK"
							labelCol={{ span: 8 }}
							name={fieldName('spkNumber')}
							rules={[rule.required()]}
						>
							<Input />
						</Form.Item>
					</Col>
				</Row>
				<Divider />
				<Form.Item
					label="Lokasi Pekerjaan"
					labelCol={{ span: 4 }}
					name={fieldName('jobLocation')}
					rules={[rule.required()]}
				>
					<Input style={{ marginLeft: '-7px' }} />
				</Form.Item>
				<Divider />
				<Form.Item
					label="Detail Kontrak"
					labelCol={{ span: 4 }}
					name={fieldName('contractDetail')}
					rules={[rule.required()]}
				>
					<TextArea
						rows={3}
						style={{ marginLeft: '-7px' }}
					/>
				</Form.Item>
				<Divider />
				<Row gutter={[40, 20]}>
					<Col span={12}>
						<Form.Item
							label="Nilai Kontrak"
							labelCol={{ span: 8 }}
							name={fieldName('contractValue')}
							rules={[rule.required()]}
						>
							<InputNumber {...inputCurrency} />
						</Form.Item>
					</Col>
					<Col span={12}>
						<Form.Item
							name={fieldName('contractDocument')}
							label="Upload Kontrak Kerja"
							labelCol={{ span: 8 }}
							style={{ verticalAlign: 'top' }}
							getValueProps={(value) => {
								const file = value as FileData;
								return {
									fileList: file?.id
										? ([
												{
													uid: file.id,
													name: file.name,
													thumbUrl: file.url,
												},
											] as UploadFile[])
										: [],
								};
							}}
							normalize={({ file }: { file: UploadFile }) => {
								return {
									id: file.uid,
									format: file.type,
									name: file.name,
									url: file.thumbUrl,
								} as FileData;
							}}
						>
							<Upload.Dragger
								listType="picture"
								accept=".jpg,.jpeg,.png"
								maxCount={1}
								beforeUpload={() => false}
								onRemove={() => {
									form.setFieldValue(fieldName('contractDocument'), null);
									return false;
								}}
								onChange={async ({ file }) => {
									const fileid = Date.now().toString();
									const filepath = `customer/${props.customerId}/${props.prospectId}/nap/employment_contract`;

									try {
										const url = await uploadMutation.mutateAsync({
											file: file as unknown as File,
											fileid,
											filepath,
										});
										form.setFieldValue(fieldName('contractDocument'), {
											format: 'jpeg',
											id: fileid,
											name: file.name,
											path: filepath,
											url,
										});
									} catch {
										form.setFieldValue(fieldName('contractDocument'), null);
									}
								}}
							>
								<UploadOutlined
									style={{
										fontSize: 25,
										color: '#BABABA',
										display: 'block',
									}}
								/>
							</Upload.Dragger>
						</Form.Item>
					</Col>
				</Row>
				<Divider />
				<Row gutter={[40, 20]}>
					<Col span={12}>
						<Form.Item
							label="Tanggal Mulai"
							labelCol={{ span: 8 }}
							name={fieldName('startDate')}
							rules={[
								rule.required(),
								{
									validator: (_, value) => {
										const endDate = form.getFieldValue(fieldName('endDate'));
										if (
											value &&
											endDate &&
											dayjs(value).isAfter(dayjs(endDate))
										) {
											return Promise.reject(
												new Error(
													'Tanggal Mulai tidak boleh lebih besar dari Tanggal Berakhir',
												),
											);
										}
										return Promise.resolve();
									},
								},
							]}
							getValueProps={(value) => ({
								value: value && dayjs(value),
							})}
							normalize={(value) => value && dayjs(value).format('YYYY-MM-DD')}
						>
							<DatePicker />
						</Form.Item>
					</Col>
					<Col span={12}>
						<Form.Item
							label="Tanggal Berakhir"
							labelCol={{ span: 8 }}
							name={fieldName('endDate')}
							rules={[
								rule.required(),
								{
									validator: (_, value) => {
										const startDate = form.getFieldValue(
											fieldName('startDate'),
										);
										if (
											value &&
											startDate &&
											dayjs(value).isBefore(dayjs(startDate))
										) {
											return Promise.reject(
												new Error(
													'Tanggal Berakhir tidak boleh kurang dari Tanggal Mulai',
												),
											);
										}
										return Promise.resolve();
									},
								},
							]}
							getValueProps={(value) => ({
								value: value && dayjs(value),
							})}
							normalize={(value) => value && dayjs(value).format('YYYY-MM-DD')}
						>
							<DatePicker />
						</Form.Item>
					</Col>
				</Row>
				<Divider />
				<Row gutter={[40, 20]}>
					<Col span={12}>
						<Form.Item
							label="Tenor"
							labelCol={{ span: 8 }}
							name={fieldName('tenor')}
							rules={[rule.required()]}
						>
							<Input />
						</Form.Item>
					</Col>
					<Col span={12}>
						<Form.Item
							name={fieldName('paymentMethod')}
							label="Sistem Pembayaran"
							labelCol={{ span: 8 }}
							rules={[rule.required()]}
							getValueFromEvent={(_, option) => option?.data}
							getValueProps={(value) => {
								if (!value) return {};
								return {
									value: {
										value: value.id,
										label: value.value,
									},
								};
							}}
						>
							<Select {...selectPaymentMethod} />
						</Form.Item>
					</Col>
				</Row>
				<Divider />
				<Row gutter={[40, 20]}>
					<Col span={12}>
						<Form.Item
							label="TOP Pembayaran"
							labelCol={{ span: 8 }}
							name={fieldName('termOfPayment')}
							rules={[rule.required()]}
						>
							<Input />
						</Form.Item>
					</Col>
				</Row>
			</Form>
		</FleetModal>
	);
};

export default EmploymentContractModal;
